import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/Button";
import { HubTouchSelect } from "@/components/ui/HubTouchSelect";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { apiFetch } from "@/lib/api";

const CHANGE_DRIVERS = [
  { value: "yield", label: "Yield" },
  { value: "quality", label: "Quality" },
  { value: "tooling", label: "Tooling" },
  { value: "design_improvement", label: "Design Improvement" },
  { value: "yield_quality", label: "Yield / Quality" },
];

const REVERSIBILITY = [
  { value: "easy", label: "Easy" },
  { value: "moderate", label: "Moderate" },
  { value: "hard", label: "Hard" },
];

const FORM_FIT = [
  { value: "no", label: "No" },
  { value: "yes", label: "Yes" },
];

type CreatedTask = {
  gid: string;
  permalinkUrl: string;
  dcrNumber: string;
  name: string;
};

const empty = {
  title: "",
  part: "",
  changeDriver: "",
  formFitFunctionImpact: "no",
  reversibility: "",
  problem: "",
  suggestedChange: "",
  statedImpact: "",
};

export function DfmChangeReviewPage(): JSX.Element {
  const { showToast } = useHubToast();
  const [draft, setDraft] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState<CreatedTask | null>(null);

  function setField(key: keyof typeof empty, value: string): void {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    try {
      const result = await apiFetch<CreatedTask>("/api/personal/dfm-change-reviews", {
        method: "POST",
        body: {
          title: draft.title,
          part: draft.part,
          changeDriver: draft.changeDriver,
          formFitFunctionImpact: draft.formFitFunctionImpact === "yes",
          reversibility: draft.reversibility,
          problem: draft.problem,
          suggestedChange: draft.suggestedChange,
          statedImpact: draft.statedImpact,
        },
      });
      setCreated(result);
      setDraft(empty);
      showToast({ tone: "success", message: `${result.dcrNumber} was added to DFM Change Review.` });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not submit the request.";
      showToast({ tone: "error", message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="space-y-4" onSubmit={(event) => void onSubmit(event)}>
      <PageTitleWithHelp hoverHint="Submits a design-for-manufacturing change request onto the DFM Change Review board in Asana, in New / Intake.">
        DFM suggestion
      </PageTitleWithHelp>
      <div className="space-y-1">
        <Label htmlFor="dfm-title">Title</Label>
        <Input id="dfm-title" value={draft.title} onChange={(event) => setField("title", event.target.value)} required />
      </div>
      <div className="space-y-1">
        <Label htmlFor="dfm-part">Part or assembly</Label>
        <Input id="dfm-part" value={draft.part} onChange={(event) => setField("part", event.target.value)} required />
      </div>
      <div className="space-y-1">
        <Label htmlFor="dfm-driver">Change driver</Label>
        <HubTouchSelect
          id="dfm-driver"
          value={draft.changeDriver}
          onChange={(value) => setField("changeDriver", value)}
          options={[{ value: "", label: "Choose a driver" }, ...CHANGE_DRIVERS]}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="dfm-fff">Form, fit, and function impact</Label>
        <HubTouchSelect
          id="dfm-fff"
          value={draft.formFitFunctionImpact}
          onChange={(value) => setField("formFitFunctionImpact", value)}
          options={FORM_FIT}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="dfm-reversibility">Reversibility</Label>
        <HubTouchSelect
          id="dfm-reversibility"
          value={draft.reversibility}
          onChange={(value) => setField("reversibility", value)}
          options={[{ value: "", label: "Choose reversibility" }, ...REVERSIBILITY]}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="dfm-problem">Problem</Label>
        <TextArea id="dfm-problem" value={draft.problem} onChange={(event) => setField("problem", event.target.value)} required rows={4} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="dfm-change">Suggested change</Label>
        <TextArea id="dfm-change" value={draft.suggestedChange} onChange={(event) => setField("suggestedChange", event.target.value)} required rows={4} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="dfm-impact">Stated impact</Label>
        <TextArea id="dfm-impact" value={draft.statedImpact} onChange={(event) => setField("statedImpact", event.target.value)} required rows={3} />
      </div>
      <Button type="submit" disabled={saving || draft.changeDriver === "" || draft.reversibility === ""}>
        {saving ? "Submitting…" : "Submit request"}
      </Button>
      {created?.permalinkUrl ? (
        <p className="text-sm text-slate-700">
          <a className="text-brand-700 underline" href={created.permalinkUrl} target="_blank" rel="noreferrer">
            Open {created.dcrNumber} in Asana
          </a>
        </p>
      ) : null}
    </form>
  );
}
